import React, {Component} from 'react';
import moment from 'moment';
import Modal from '../../../shared/ui/Modal';
import {ApiGetRaw} from '../../../shared/services/api-service';
import DwCalendar from './DwCalendar';
import {groupAssignments} from './fto-calendar-events';
import './FtoCalendarPage.scss';

class FtoCalendarPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      assignments: [],
      from: moment().startOf('month').startOf('week').format('YYYY-MM-DD'),
      to: moment().endOf('month').endOf('week').format('YYYY-MM-DD'),
      includeInactive: false,
      oit: '',
      fto: '',
      phase: '',
      loading: true,
      error: '',
      selectedEvent: null,
    };
    this.requestId = 0;
  }

  componentDidMount() {
    this.fetchAssignments();
  }

  componentWillUnmount() {
    this.requestId += 1;
  }

  fetchAssignments() {
    const requestId = ++this.requestId;
    const {from, to, includeInactive} = this.state;
    const start = encodeURIComponent(moment(from).startOf('day').toISOString());
    const end = encodeURIComponent(moment(to).add(1, 'day').startOf('day').toISOString());
    this.setState({loading: true, error: '', assignments: [], selectedEvent: null});
    ApiGetRaw(`/api/v1/fto-calendar?from=${start}&to=${end}&includeInactive=${includeInactive}`)
      .then(({data}) => {
        if (requestId === this.requestId) this.setState({assignments: data, loading: false});
      })
      .catch(() => {
        if (requestId === this.requestId) this.setState({loading: false, error: 'Unable to load assignments.'});
      });
  }

  options(field) {
    const options = new Map();
    this.state.assignments.forEach((assignment) => {
      const person = assignment[field];
      if (person) options.set(field === 'phase' ? person.name : person.guid, person.name);
    });
    return Array.from(options.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }

  render() {
    const {oit, fto, phase, loading, error, selectedEvent, includeInactive} = this.state;
    const events = groupAssignments(this.state.assignments).filter(event =>
      (!oit || event.oit.guid === oit) &&
      (!fto || (fto === 'unassigned' ? !event.fto : event.fto && event.fto.guid === fto)) &&
      (!phase || event.phase.name === phase));
    const legend = new Map(events.map(event => [event.fto ? event.fto.guid : 'unassigned', {
      name: event.fto ? event.fto.name : 'Unassigned',
      color: event.color,
    }]));
    const phaseLegend = new Map(events.map(event => [event.phase.guid, {
      name: event.phase.name,
      color: event.phaseColor,
    }]));

    return (
      <div className="fto-calendar-page">
        <div className="app-content-header"><h4 className="title">Master FTO Calendar</h4></div>
        <div className="app-content">
          <p>Daily report assignments for your current division.</p>
          <div className="calendar-filters">
            {[['oit', 'OIT'], ['fto', 'FTO'], ['phase', 'Phase']].map(([field, label]) => (
              <label key={field} htmlFor={`calendar-${field}`}>
                {label}
                <select id={`calendar-${field}`} value={this.state[field]}
                  onChange={event => this.setState({[field]: event.target.value})}>
                  <option value="">All {label === 'Phase' ? 'phases' : `${label}s`}</option>
                  {field === 'fto' && <option value="unassigned">Unassigned</option>}
                  {this.state[field] && !this.options(field).some(([guid]) => guid === this.state[field]) &&
                    this.state[field] !== 'unassigned' && <option value={this.state[field]}>Selected {label} (no assignments)</option>}
                  {this.options(field).map(([guid, name]) => <option key={guid} value={guid}>{name}</option>)}
                </select>
              </label>
            ))}
            <label className="include-inactive">
              <input type="checkbox" checked={includeInactive} onChange={event =>
                this.setState({includeInactive: event.target.checked}, () => this.fetchAssignments())}/>
              Include completed, terminated, and inactive OITs
            </label>
          </div>
          <div className="fto-legend">
            {Array.from(legend.entries()).map(([guid, fto]) => (
              <span key={guid}><span className="fto-color" style={{backgroundColor: fto.color}}/>{fto.name}</span>
            ))}
          </div>
          <div className="phase-legend">
            {Array.from(phaseLegend.entries()).map(([guid, phase]) => (
              <span key={guid}><span className="phase-color-dot" style={{backgroundColor: phase.color}}/>{phase.name}</span>
            ))}
          </div>
          <div role="status" aria-live="polite">
            {loading && <p>Loading assignments…</p>}
            {!loading && !error && events.length === 0 && <p>No assignments match this date range and filters.</p>}
          </div>
          {error && <div role="alert">{error} <button type="button" className="button small primary"
            onClick={() => this.fetchAssignments()}>Retry</button></div>}
          <DwCalendar events={events} canModify={false} defaultView="MONTH" defaultDate={new Date()}
            showEditModal={event => this.setState({selectedEvent: event})}
            updateEventsForDateRange={(from, to) => this.setState({from, to}, () => this.fetchAssignments())}/>
          {selectedEvent && <Modal isOpen title="Daily assignment details" onClose={() => this.setState({selectedEvent: null})}>
            <div className="assignment-details">
              <p><b>Date:</b> {moment(selectedEvent.date).format('MMM D, YYYY')}</p>
              <p><b>OIT:</b> {selectedEvent.oit.name}</p>
              <p><b>FTO:</b> {selectedEvent.fto ? selectedEvent.fto.name : 'Unassigned'}</p>
              <p><b>Phase:</b> {selectedEvent.phase.name}</p>
              <p><b>Training status:</b> {selectedEvent.trainingStatus}</p>
              <table className="table">
                <thead><tr><th>Report</th><th>Status</th><th>FTO assignment</th><th>Other submitter</th></tr></thead>
                <tbody>{selectedEvent.reports.map(report => <tr key={report.guid}>
                  <td>{report.type.toUpperCase()}</td><td>{report.status}</td>
                  <td>{!report.fto ? 'Unassigned' : report.ftoSource === 'phase' ? 'Phase FTO (fallback)' : 'Report FTO'}</td>
                  <td>{report.submitter ? report.submitter.name : '—'}</td>
                </tr>)}</tbody>
              </table>
            </div>
          </Modal>}
        </div>
      </div>
    );
  }
}

export default FtoCalendarPage;
